import { PERSONA } from "../data/lines.js";
import { SHOP } from "../data/items.js";
import { activeMute } from "../rules.js";
import { addDays, dayKey, formatHm, type DayKey } from "../time.js";
import type { Area, GameState, Quest, Size } from "../types.js";
import type { LLMProvider, LLMRequest, LLMResponse } from "./brain.js";
import { parsePolishDate, parsePolishTime } from "./dates.js";
import { quickIntent } from "./quick.js";
import type { ToolCall } from "./toolcalls.js";

/**
 * Rule-based stand-in for the LLM: Polish keyword NLU producing the same tool calls a model would.
 * Used in CI evals (to test the harness), in local demo mode and when no API key is configured.
 * Matching runs on text without diacritics (same length, char for char), extraction slices the original.
 */
export class MockProvider implements LLMProvider {
  readonly name = "mock";

  async chat(req: LLMRequest): Promise<LLMResponse> {
    const last = [...req.turns].reverse().find((t) => t.role === "user");
    const prev = [...req.turns].reverse().find((t) => t.role === "assistant");
    if (!req.meta || !last || last.role !== "user") return toResponse("Hmm?", []);
    const out = understand(
      last.text,
      req.meta.state,
      req.meta.now,
      prev?.role === "assistant" ? prev.text : "",
    );
    return toResponse(out.text, out.calls);
  }
}

function toResponse(text: string, calls: ToolCall[]): LLMResponse {
  return {
    text,
    toolCalls: calls,
    usage: { inputTokens: 0, outputTokens: 0 },
    stop: calls.length ? "tool_use" : "end",
  };
}

const PL: Record<string, string> = { ą: "a", ć: "c", ę: "e", ł: "l", ń: "n", ó: "o", ś: "s", ź: "z", ż: "z" };
export function deaccent(s: string): string {
  return s.replace(/[ąćęłńóśźż]/g, (c) => PL[c] ?? c);
}

/** Text pair: `a` for matching (ASCII-ish), `o` original lowercase for extraction. */
interface Txt {
  a: string;
  o: string;
}
const txt = (s: string): Txt => {
  const o = s
    .toLowerCase()
    .replace(/[!?…"„”]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return { a: deaccent(o), o };
};
const has = (t: Txt, re: RegExp) => re.test(t.a);
function grab(t: Txt, re: RegExp): (string | undefined)[] | null {
  const m = new RegExp(re.source, re.flags.replace("d", "") + "d").exec(t.a);
  if (!m?.indices) return null;
  return m.indices.map((ix) => (ix ? t.o.slice(ix[0], ix[1]) : undefined));
}
function slice(t: Txt, from: number, to?: number): Txt {
  return { a: t.a.slice(from, to), o: t.o.slice(from, to) };
}
function split(t: Txt, re: RegExp): Txt[] {
  const out: Txt[] = [];
  const g = new RegExp(re.source, "g");
  let last = 0;
  for (const m of t.a.matchAll(g)) {
    out.push(slice(t, last, m.index));
    last = m.index + m[0].length;
  }
  out.push(slice(t, last));
  return out.map((x) => ({ a: x.a.trim(), o: x.o.trim() })).filter((x) => x.a);
}

const AREA_WORDS: [Area, RegExp][] = [
  // People first: "spacer z kumplem" or "piwo ze znajomymi" is Relacje, not Ciało.
  [
    "social",
    /znajom|kumpl|przyjaci|kolez|rodzin|rodzic|\bmam[aey]\b|\bmama\b|\btat[aey]\b|babci|dziadk|siostr|\bbrat|randk|imprez|urodzin|odwiedzi|\bpiwo|\bna piwo|\bna kaw|kawiar/,
  ],
  [
    "body",
    /trening|silk|silc|silow|bieg|biega|spacer|rower|pomp|przysiad|basen|plyw|joga|joge|rozciag|cwicz|\bkm\b|mecz|tenis|squash|wspinac|brzuszk|plank|trenin|gym/,
  ],
  [
    "mind",
    /czyta|czyt|ksiaz|rozdzia|kurs|nauk|uczy|uczyl|lekcj|angiel|jezyk|medyt|podcast|wyklad|fiszk|egzamin|studi/,
  ],
  [
    "work",
    /ticket|tiket|prac|robot|projekt|klient|spotkan|raport|prezentac|\bkod|deploy|faktur|zarobi|biznes|szef|zlecen|integrac|\bbug|feature|\bcall|odcin|youtub|\byt\b|montaz|nagral|\bfilm|artykul/,
  ],
  [
    "home",
    /smie|sprzat|pranie|prani|zmyw|odkurz|kuchn|lazien|zakup|gotow|obiad|kolac|sniad|urzad|rachun|ksiegow|urzed|mechanik|napraw|podla|posciel|naczyni|okna|porzad|wynie|wynos|prasow|mieszkan|\bdom/,
  ],
];

const ANIM_WORDS: [RegExp, string][] = [
  [/piw|\bbar\b|\bpub/, "beer"],
  [/\bkaw|kawiar/, "coffee"],
  [/smie|wynie|wynos/, "trash"],
  [/gotow|obiad|kolac|sniad/, "cook"],
  [/zakup|sklep/, "shop"],
  [/sprzat|odkurz|zmyw|naczyni|pranie|prasow|porzad|kuchn|lazien/, "clean"],
  [/pisa|notat|maila|\blist/, "write"],
  [/czyta|czyt|ksiaz|rozdzia/, "read"],
];

const STOP = new Set([
  "jutro",
  "dzisiaj",
  "teraz",
  "jednak",
  "przesun",
  "wywal",
  "zrobione",
  "zrobilem",
  "musze",
  "chce",
  "trzeba",
  "jestem",
  "bedzie",
  "tylko",
  "troche",
  "chyba",
  "potem",
  "przypomnij",
]);

const PAST =
  /\p{L}+(lem|lam|les|las|nelam|lismy|lysmy)\b|\p{L}{3,}(ione|iony|ona|one|ete|ane|any|ony|iete|ieta)\b|\bbyl(em|am)\b|\bzrobion|\bgotowe\b|\bogarniete\b/u;
const NEGATED =
  /\bnie \p{L}*(lem|lam)\b|\bnic\b|^jestem\b|\bjestem (dzis |dzisiaj |strasznie |bardzo |troche )*\p{L}+(ony|ona|ana|any)\b/u;
const PLAN =
  /\bjutr|\bmusze\b|\bchce\b|\btrzeba\b|\bplanuj|\bzamierzam|\bpojde\b|\bmam zamiar|\bpowinien|\bpowinnam|\bco \p{L}+ dni|\bco \d+ dni|\bcodziennie|\bw planie|\bsprobuje|\bwybiore|\bzrobie\b|\bpojade\b/u;

function areaOf(a: string): Area | null {
  for (const [area, re] of AREA_WORDS) if (re.test(a)) return area;
  return null;
}

function animationOf(a: string, area: Area): string {
  for (const [re, anim] of ANIM_WORDS) if (re.test(a)) return anim;
  return { body: "train", mind: "read", work: "work", social: "coffee", home: "clean" }[area];
}

function sizeOf(a: string): Size {
  const n = /\b(\d+)\s*(pompek|przysiad|brzuszk|minut|min\b)/.exec(a);
  if (n) return Number(n[1]) >= 45 && /min/.test(n[2]!) ? "M" : "S";
  if (/ciezk|dlugi|mocn|godzin|caly dzien|maraton|w koncu|duzy|ogromn|wielki/.test(a)) return "L";
  if (/troche|szybk|chwil|drobn|\bmale\b|krotk|smieci|wynioslem|naczyni|zmyw|podlal|posciel/.test(a))
    return "S";
  return "M";
}

function significant(a: string): string[] {
  return a
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .split(" ")
    .filter((w) => w.length >= 4 && !STOP.has(w));
}

function wordsMatch(x: string, y: string): boolean {
  const need = Math.max(3, Math.min(5, Math.min(x.length, y.length) - 1));
  let i = 0;
  while (i < x.length && i < y.length && x[i] === y[i]) i++;
  return i >= need;
}

function matchQuest(a: string, quests: readonly Quest[]): Quest | null {
  const cw = significant(a);
  let best: { q: Quest; score: number } | null = null;
  for (const q of quests) {
    if (q.status !== "open") continue;
    const score = significant(deaccent(q.title.toLowerCase())).filter((w) =>
      cw.some((c) => wordsMatch(w, c)),
    ).length;
    if (score > 0 && (!best || score > best.score)) best = { q, score };
  }
  return best?.q ?? null;
}

const RATINGS: [RegExp, number][] = [
  [/\b(1|jeden|jedynk\p{L}*|raz)\b/u, 1],
  [/\b(2|dwa|dwojk\p{L}*)\b/u, 2],
  [/\b(3|trzy|trojk\p{L}*)\b/u, 3],
  [/\b(4|cztery|czwork\p{L}*)\b/u, 4],
  [/\b(5|piec|piatk\p{L}*)\b/u, 5],
];

const DISHES: [RegExp, string][] = [
  [/pesto|makaron/, "makaron z pesto"],
  [/pierog/, "pierogi"],
  [/pomidorow/, "pomidorowa"],
  [/nalesnik/, "naleśniki"],
];

const CLAUSE_SEP = /,\s*(?:a\s+)?|\s+(?:i|oraz|a|potem|a potem|pozniej)\s+|[.;]\s*/;
const PLAN_WORDS =
  /\b(dzisiaj|dzis|jutro|chyba|chce|musze|trzeba|mam zamiar|planuje|zamierzam|powinienem|powinnam|sprobuje)\b/g;
const FILLER_WORDS = /\b(dzisiaj|dzis|w koncu|wreszcie|juz|troche|ten|te|ta|zrobilem|zrobilam)\b/g;

/** Remove words (found in `a`) from the original text, keeping diacritics elsewhere. */
function strip(t: Txt, re: RegExp): string {
  let o = t.o;
  const matches = [...t.a.matchAll(new RegExp(re.source, re.flags.includes("u") ? "gu" : "g"))].reverse();
  for (const m of matches) o = o.slice(0, m.index) + o.slice(m.index + m[0].length);
  return o.replace(/\s+/g, " ").trim();
}

const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

const IRREGULAR: Record<string, string> = {
  wyniósł: "wynieść",
  wyniosła: "wynieść",
  poszedł: "iść",
  poszła: "iść",
  wziął: "wziąć",
  wzięła: "wziąć",
  wyjął: "wyjąć",
  wyjęła: "wyjąć",
  zjadł: "zjeść",
  zjadła: "zjeść",
  przyniósł: "przynieść",
  przyniosła: "przynieść",
};

/** "żebym wyniósł śmieci" -> "wynieść śmieci": past-tense forms after "żebym" back to the infinitive. */
export function toInfinitive(phrase: string): string {
  return phrase
    .split(" ")
    .map((w, i) => {
      if (i > 1) return w;
      if (IRREGULAR[w]) return IRREGULAR[w];
      const m = /^(\p{L}{2,})(i|a|y|e)ła?$/u.exec(w);
      return m ? `${m[1]}${m[2]}ć` : w;
    })
    .join(" ");
}

export function understand(
  raw: string,
  state: GameState,
  now: number,
  lastAssistant = "",
): { text: string; calls: ToolCall[] } {
  const calls: ToolCall[] = [];
  const said: string[] = [];
  const add = (name: string, input: Record<string, unknown>) =>
    calls.push({ id: `mock-${calls.length + 1}`, name, input });
  const today = dayKey(now, state.settings.timezone);
  const t = txt(raw);
  const words = t.a
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .trim()
    .split(/\s+/);

  const quick = quickIntent(raw, state, now);
  if (quick) {
    for (const act of quick.actions) {
      if (act.type === "mute")
        add("mute", { kind: act.kind, ...(act.untilDay ? { until: act.untilDay } : {}) });
      else if (act.type === "unmute") add("unmute", {});
    }
    return { text: quick.text, calls };
  }

  // Weekly rating answer.
  const lp = state.nudge.lastPrompt;
  if ((lp?.kind === "weekly" || has(t, /ocen|progres/)) && words.length <= 5) {
    for (const [re, v] of RATINGS)
      if (has(t, re)) {
        add("rate_week", { value: v });
        return {
          text:
            v >= 4
              ? "Też to widzę. Dobra robota w tym tygodniu."
              : "Dzięki, że mówisz wprost. Nowy tydzień zaczniemy od małego kroku.",
          calls,
        };
      }
  }

  // Mutes the quick path didn't catch (longer sentences).
  if (has(t, /nie gadaj|nie odzywaj|wylacz sie|nie zagaduj/)) {
    const until = parsePolishDate(t.o, today) ?? addDays(today, 3);
    add("mute", { kind: "days", until });
    return { text: "Dobra, daję ci spokój. Trzymaj się.", calls };
  }
  if (has(t, /daj mi (dzis |dzisiaj )?spokoj|nie teraz/)) {
    add("mute", { kind: "today" });
    return { text: "Jasne, nie przeszkadzam.", calls };
  }
  if (has(t, /^(mozesz|juz mozesz).*(gadac|mowic|odzywac)|^wroc\b|^wracaj\b/)) {
    add("unmute", {});
    return { text: "Jestem z powrotem. Jak się masz?", calls };
  }

  // Going to bed: the companion goes to sleep too (companion_do).
  if (
    has(t, /\bide spac|\bidziemy spac|\bklade sie|\blece spac|\bdobranoc|\bpora spac/) &&
    words.length <= 8
  ) {
    add("companion_do", { activity: "sleep" });
    return { text: "Dobranoc! Ja też się kładę.", calls };
  }

  // Modes.
  if (has(t, /juz mi lepiej|wyzdrowia|koniec urlopu|wrocil\p{L}* z urlopu|wracam do normy|koniec choroby/u)) {
    add("set_mode", { mode: "normal" });
    return { text: "Super, wracamy do gry. Jedna mała rzecz na dziś?", calls };
  }
  if (has(t, /\bchor|gryp|przezie|goraczk|rozlozyl|rozwalil|przeziebi/)) {
    add("set_mode", { mode: "sick" });
    return { text: "Kuruj się spokojnie, ja pilnuję domu.", calls };
  }
  if (has(t, /urlop|wakacj/)) {
    const m = grab(t, /\bdo (\p{L}+|\d{1,2}\.\d{1,2})/u);
    const end = m ? parsePolishDate(m[0]!, today) : null;
    add("set_mode", { mode: "vacation", ...(end ? { until: addDays(end, 1) } : {}) });
    return { text: "Udanego urlopu, odpoczywaj na całego.", calls };
  }
  if (
    has(t, /zawal|zawalon|mlyn|urwanie glowy|crunch|nawal|deadline/) &&
    has(t, /robo|prac|crunch|deadline/)
  ) {
    add("set_mode", { mode: "crunch" });
    return { text: "Rozumiem, w takim razie biorę tylko małe rzeczy.", calls };
  }

  // Facts.
  const fact = grab(t, /zapamietaj,?\s*(?:ze\s+)?(.+)/);
  if (fact) {
    add("remember_fact", { fact: fact[1]!.trim() });
    return { text: "Zapamiętam.", calls };
  }
  const forget = grab(t, /zapomnij,?\s*(?:o tym,?\s*)?(?:ze\s+)?(.+)/);
  if (forget) {
    const words3 = (x: string) =>
      deaccent(x.toLowerCase())
        .split(/[^\p{L}]+/u)
        .filter((w) => w.length >= 3 && w !== "nie");
    const fw = words3(forget[1]!);
    const f = state.facts.find((x) => words3(x.text).some((w) => fw.some((c) => wordsMatch(w, c))));
    if (f) {
      add("forget_fact", { id: f.id });
      return { text: "Jasne, zapominam.", calls };
    }
  }

  // Food chat and the shopping list that follows.
  if (
    has(
      t,
      /co bys (dzis |dzisiaj )?zjadl|co zjemy|co na obiad|co (dzis |dzisiaj )?jemy|co ugotowac|co bys zjadla|na co masz ochote/,
    )
  ) {
    const avoid = state.facts.map((f) => f.text.toLowerCase()).join(" ");
    const dish =
      PERSONA.likes.find((d) => !(avoid.includes("ryb") && d.includes("ryb"))) ?? PERSONA.likes[0]!;
    return { text: `Zjadłbym ${dish}. Dopisać składniki do listy zakupów?`, calls };
  }
  const la = deaccent(lastAssistant.toLowerCase());
  if (
    la &&
    /zjadlbym|skladnik/.test(la) &&
    has(t, /dobra|\btak\b|zrobmy|dopisz|jasne|\bok\b|okej|pewnie|chetnie/)
  ) {
    const key = DISHES.find(([re]) => re.test(la))?.[1];
    const items = key ? PERSONA.dishes[key] : undefined;
    if (items) {
      add("shopping_add", { items });
      return { text: "Dopisane na lodówce. Smacznego zawczasu!", calls };
    }
  }

  // Shopping list.
  if (
    has(t, /wyczysc (cala )?list|zakupy zrobione|wyczysc zakupy|lista do kosza|zrobilem zakupy,? wyczysc/)
  ) {
    add("shopping_clear", {});
    return { text: "Lista na lodówce wyczyszczona.", calls };
  }
  const shopAdd =
    grab(t, /(?:dopisz|dodaj|wpisz|zapisz)\s+(.+?)\s+(?:do|na)\s+(?:listy\s+)?(?:zakupow|listy|lodowk)/) ??
    grab(t, /(?:skonczyl\p{L}* sie|nie ma juz|brakuje nam|musimy kupic|trzeba kupic)\s+(.+)/u);
  if (shopAdd) {
    const items = shopAdd[1]!
      .split(/,\s*|\s+i\s+|\s+oraz\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    add("shopping_add", { items });
    return { text: "Dopisane na lodówce.", calls };
  }
  const shopRemove = grab(t, /(?:usun|skresl|wykresl|wywal)\s+(.+?)\s+z\s+(?:listy|zakupow)/);
  if (shopRemove) {
    add("shopping_remove", { items: shopRemove[1]!.split(/,\s*|\s+i\s+/).map((s) => s.trim()) });
    return { text: "Skreślone.", calls };
  }
  const bought = state.shopping.filter(
    (s) => t.a.includes(deaccent(s.text.toLowerCase())) && has(t, /kupi|kupion|mam juz/),
  );
  if (bought.length) {
    add("shopping_remove", { items: bought.map((b) => b.text) });
    return { text: "Skreślone z listy.", calls };
  }

  // Shop.
  if (has(t, /\bkup(my|cie|ic|my mu|my sobie)?\b|\bkup sobie\b|\bkupimy\b/)) {
    const item = SHOP.find(
      (s) => t.a.includes(deaccent(s.name.split(" ")[0]!.slice(0, 5))) || t.a.includes(s.id),
    );
    if (item) {
      if (state.inventory.some((i) => i.id === item.id))
        return { text: `Przecież ${item.name} już stoi w mieszkaniu.`, calls };
      if (state.stats.gold >= item.price) {
        add("buy_item", { item_id: item.id });
        return { text: `Kupione! Zaraz wniosę ${item.name}.`, calls };
      }
      return {
        text: `Na ${item.name} brakuje jeszcze ${item.price - state.stats.gold} złota. Uzbieramy!`,
        calls,
      };
    }
  }

  // Real money (never store or repeat amounts).
  if (has(t, /premi|podwyzk|bonus|zarobil|wygral|dostal\p{L}* kas|przelew|nadgodzin|trzynast/u)) {
    const k = /(\d+(?:[.,]\d+)?)\s*(k\b|tys)/.exec(t.a);
    const plain = /(\d{2,})/.exec(t.a);
    const value = k ? Number(k[1]!.replace(",", ".")) * 1000 : plain ? Number(plain[1]) : NaN;
    const tier =
      has(t, /premi|podwyzk|trzynast/) || value >= 1000 ? "large" : value < 200 ? "small" : "medium";
    add("money_event", {
      tier,
      note: has(t, /premi/) ? "premia" : has(t, /podwyzk/) ? "podwyżka" : "dodatkowy zarobek",
    });
    if (has(t, /zarobi|nadgodzin|zlecen|dodatkow/))
      add("log_activity", { area: "work", text: "dodatkowy zarobek", size: "L", animation: "work" });
    return { text: "Świetna wiadomość! Do sakiewki wpada złoto, świętujemy.", calls };
  }

  // Quest management.
  const open = state.quests.filter((q) => q.status === "open");
  if (has(t, /wywal|rezygnuj|odpuszczam|skresl|usun to|jednak nie|nieaktualn|olewam/)) {
    const q = matchQuest(t.a, open) ?? (lp?.questId ? (open.find((x) => x.id === lp.questId) ?? null) : null);
    if (q) {
      add("drop_quest", { id: q.id });
      return { text: `Wywalone: ${q.title}. Bez żalu.`, calls };
    }
  }
  if (has(t, /przesun|nie dam rady|przeloz|na pozniej|nie zdaze|nie zdazy/)) {
    const q = matchQuest(t.a, open) ?? (lp?.questId ? (open.find((x) => x.id === lp.questId) ?? null) : null);
    if (q) {
      const to = parsePolishDate(strip(t, /\b(dzis|dzisiaj)\b/), today);
      add("postpone_quest", { id: q.id, ...(to && to !== today ? { to } : {}) });
      return { text: `Przesunięte: ${q.title}. Bez spiny.`, calls };
    }
  }
  if (
    has(t, /^(zrobione|zrobilem|zrobilam|gotowe|ogarniete|jest|done|juz|tak zrobilem|tak,? zrobione)$/) &&
    lp?.questId
  ) {
    const q = open.find((x) => x.id === lp.questId);
    if (q) {
      add("complete_quest", { id: q.id });
      return { text: `Jest! ${q.title} odhaczone.`, calls };
    }
  }

  // Vent.
  if (has(t, /nic (dzis |dzisiaj )?nie zrobil|jestem do niczego|nic mi nie wychodzi|zmarnowal|beznadziej/)) {
    return { text: "Hej, bez oceniania. Opowiedz, co dziś się działo, na pewno coś się znajdzie.", calls };
  }

  // Reminder: everything after "przypomnij" belongs to it.
  let rest = t;
  const rIdx = t.a.search(/przypomnij|przypomnienie/);
  if (rIdx >= 0) {
    rest = slice(t, 0, rIdx);
    rest = {
      a: rest.a.replace(/[\s,]*(i|a|oraz)?[\s,]*$/, ""),
      o: rest.o.slice(0, rest.a.replace(/[\s,]*(i|a|oraz)?[\s,]*$/, "").length),
    };
    const rem = slice(t, rIdx);
    const minutes = parsePolishTime(rem.o);
    const part = /wiecz/.test(rem.a)
      ? "wieczorem"
      : /\brano\b/.test(rem.a)
        ? "rano"
        : /po poludniu/.test(rem.a)
          ? "po południu"
          : "";
    const at = minutes !== null ? formatHm(minutes) : part;
    const date = parsePolishDate(rem.o, today);
    const what = strip(
      rem,
      /przypomnij(cie)?( mi)?|przypomnienie|\b(o|na) \d{1,2}([:.]\d{2})?\b|\b(zebym|zeby|ze|abym|aby|o tym|jutro|pojutrze|wieczorem|rano|po poludniu|dzisiaj|dzis|musze|mam)\b/,
    ).replace(/^[\s,]+|[\s,]+$/g, "");
    const atOut =
      date && date !== today ? (minutes !== null ? `${date}T${at}` : `${part || "rano"} ${date}`) : at;
    add("add_reminder", { text: toInfinitive(what) || "przypomnienie", at: atOut });
    said.push(
      minutes !== null ? `Przypomnę ci o ${at}.` : part ? `Przypomnę ci ${part}.` : "Przypomnę ci wieczorem.",
    );
  }

  // Clause by clause: things done vs plans.
  const planSentence = PLAN.test(rest.a);
  const logged: string[] = [];
  const planned: string[] = [];
  const completed: string[] = [];
  for (const clause of split(rest, CLAUSE_SEP)) {
    const c = clause.a.replace(/^(a|i|no|dobra|to)\s+/, "");
    const off = clause.a.length - c.length;
    const co = clause.o.slice(off);
    if (!c || /^(dzis|dzisiaj|jutro)$/.test(c)) continue;
    const area = areaOf(c);
    const past = PAST.test(c) && !NEGATED.test(c);
    const plan = PLAN.test(c) || (planSentence && !past);
    if (past) {
      const q = matchQuest(c, open);
      if (q && !calls.some((x) => x.name === "complete_quest" && x.input.id === q.id)) {
        add("complete_quest", { id: q.id });
        completed.push(q.title);
        continue;
      }
      if (!area) continue;
      const text = strip({ a: c, o: co }, FILLER_WORDS).split(" ").slice(0, 6).join(" ");
      add("log_activity", { area, text, size: sizeOf(c), animation: animationOf(c, area) });
      logged.push(text);
    } else if (plan && (area || /\p{L}{2,}c\b/u.test(c))) {
      const rep = /co (\d+|\p{L}+) dni/u.exec(c);
      const repWords: Record<string, number> = { dwa: 2, trzy: 3, cztery: 4, piec: 5, siedem: 7, kilka: 3 };
      const repeat = rep
        ? Number(rep[1]) || repWords[rep[1]!]
        : /codziennie/.test(c)
          ? 1
          : /co tydzien/.test(c)
            ? 7
            : undefined;
      const tomorrow = /jutr/.test(c) || (/jutr/.test(rest.a) && !/\bdzis/.test(c));
      const due: DayKey | "today" | "tomorrow" = tomorrow
        ? "tomorrow"
        : (parsePolishDate(co, today) ?? "today");
      const a = area ?? "home";
      const title = cap(
        strip({ a: c, o: co }, new RegExp(`${PLAN_WORDS.source}|\\bco \\p{L}+ dni\\b|\\bcodziennie\\b`, "u")),
      );
      add("add_quest", {
        title,
        area: a,
        size: a === "body" ? "M" : "S",
        due,
        ...(repeat ? { repeat_days: repeat } : {}),
      });
      planned.push(title.toLowerCase());
    }
  }
  if (completed.length) said.unshift(`Jest! Odhaczone: ${completed.join(", ")}.`);
  if (logged.length)
    said.unshift(
      logged.length > 1 ? "Ale dzień! Wszystko zapisane." : `Zapisane: ${logged[0]}. Dobra robota!`,
    );
  if (planned.length)
    said.push(
      planned.length > 1 ? `Plan: ${planned.slice(0, 3).join(", ")}.` : `Dopisane do planu: ${planned[0]}.`,
    );
  if (!said.length) {
    if (activeMute(state.mute, now)) return { text: "Jestem tu, po cichu.", calls };
    return {
      text: /\?\s*$/.test(raw) ? "Dobre pytanie. A co u ciebie dziś?" : "Opowiesz coś więcej?",
      calls,
    };
  }
  return { text: said.join(" "), calls };
}
